import { scaleLinear, scalePoint } from 'd3-scale';
import { area, curveLinear, line } from 'd3-shape';
import { cx } from '../cx';
import { Tooltip } from '../Tooltip/Tooltip';
import { type ChartFocus, ChartFrame, type ChartTable } from './ChartFrame';
import {
  AXIS_GAP_PX,
  AXIS_LINE_PX,
  type ChartPoint,
  type ChartSeries,
  categoriesOf,
  estimateTextWidth,
  fillClass,
  formatValue,
  labelBudget,
  labelStep,
  MAX_GUTTER_SHARE,
  NO_DATA,
  PLOT_PAD_PX,
  seriesColor,
  strokeClass,
  truncateLabel,
  valueAt,
  valueDomain,
  valueTicks,
} from './chartModel';

/**
 * A LINE, OR AN AREA UNDER IT, OVER ONE OR MANY SERIES (`reports.md` T1).
 *
 * STRAIGHT SEGMENTS, NOT A SMOOTHED CURVE. `curveLinear` is the only honest generator for
 * a monthly series: a spline through six monthly totals invents values between the months
 * that nobody measured, and the eye reads the invented overshoot as data.
 *
 * A NULL IS A GAP, NOT A DIP. `defined()` breaks the path where a bucket has no number, so
 * a month we could not read leaves a hole rather than a plunge to the floor - the same
 * distinction the data model makes between "no data" and "zero".
 */
export interface LineChartProps {
  readonly name: string;
  readonly titleHidden?: boolean | undefined;
  readonly series: readonly ChartSeries[];
  /** Fills the space between the line and the zero line. Only honest for ONE series. */
  readonly area?: boolean | undefined;
  readonly height?: number | undefined;
  readonly loading?: boolean | undefined;
  readonly refetching?: boolean | undefined;
  readonly emptyMessage?: string | undefined;
  readonly categoryHeading?: string | undefined;
  readonly format?: ((value: number) => string) | undefined;
}

export function LineChart({
  name,
  titleHidden,
  series,
  area: filled = false,
  height = 240,
  loading = false,
  refetching = false,
  emptyMessage,
  categoryHeading = 'Period',
  format = formatValue,
}: LineChartProps) {
  const categories = categoriesOf(series);
  const empty = categories.length === 0 || series.length === 0;

  const table: ChartTable = {
    categoryHeading,
    columns: series.map((entry) => entry.label),
    rows: categories.map((category) => ({
      label: category.label,
      values: series.map((entry) => valueAt(entry, category.key)),
    })),
  };

  // A legend of one is furniture: the chart's own name already says what the one series is.
  const legend =
    series.length > 1
      ? series.map((entry, index) => ({
          id: entry.id,
          label: entry.label,
          color: seriesColor(entry.tone, index, series.length),
        }))
      : [];

  const announce = ({ series: seriesIndex, point }: ChartFocus): string => {
    const entry = series[seriesIndex];
    const category = categories[point];
    if (entry === undefined || category === undefined) return '';
    const value = valueAt(entry, category.key);
    return `${entry.label}, ${category.label}: ${value === null ? NO_DATA : format(value)}`;
  };

  return (
    <ChartFrame
      name={name}
      {...(titleHidden === undefined ? {} : { titleHidden })}
      height={height}
      loading={loading}
      refetching={refetching}
      {...(emptyMessage === undefined ? {} : { emptyMessage })}
      empty={empty}
      legend={legend}
      table={table}
      rows={series.length}
      columns={categories.length}
      announce={announce}
    >
      {({ width, height: plotHeight, focus }) => (
        <LinePlot
          width={width}
          height={plotHeight}
          series={series}
          categories={categories}
          filled={filled}
          focus={focus}
          format={format}
          animate={!refetching}
        />
      )}
    </ChartFrame>
  );
}

function LinePlot({
  width,
  height,
  series,
  categories,
  filled,
  focus,
  format,
  animate,
}: {
  readonly width: number;
  readonly height: number;
  readonly series: readonly ChartSeries[];
  readonly categories: readonly ChartPoint[];
  readonly filled: boolean;
  readonly focus: ChartFocus | null;
  readonly format: (value: number) => string;
  readonly animate: boolean;
}) {
  const values = series.flatMap((entry) =>
    entry.points.map((point) => point.value).filter((value): value is number => value !== null),
  );
  const domain = valueDomain(values);
  const ticks = valueTicks(domain, 4);

  /** Capped, then the labels are cut to the cap: the same rule the bar chart states. */
  const valueGutter = Math.min(
    Math.max(...ticks.map((tick) => estimateTextWidth(format(tick)))) + AXIS_GAP_PX,
    width * MAX_GUTTER_SHARE,
  );

  const left = valueGutter;
  const top = PLOT_PAD_PX;
  const bottom = AXIS_LINE_PX + AXIS_GAP_PX;
  const plotWidth = Math.max(1, width - left - PLOT_PAD_PX);
  const plotHeight = Math.max(1, height - top - bottom);

  /**
   * A POINT SCALE, NOT A BAND. A line's marks sit ON the category, not across it - the
   * first point belongs at the left edge and the last at the right, which is what
   * `scalePoint` gives and what a band scale deliberately does not.
   */
  const x = scalePoint<string>()
    .domain(categories.map((category) => category.key))
    .range([left, left + plotWidth])
    .padding(0.5);

  const y = scaleLinear()
    .domain([...domain])
    .range([top + plotHeight, top]);

  const at = (key: string): number => x(key) ?? left;

  const step = labelStep(categories.length, plotWidth, 3 * estimateTextWidth('MMM'));
  const zero = y(0);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      aria-hidden="true"
      /**
       * `w-full` ON TOP OF THE WIDTH ATTRIBUTE, and it is the same bug one level out that
       * `ChartFrame`'s own `w-full` fixes. An `<svg width="640">` has an INTRINSIC width of
       * 640, so every ancestor sized by its content - a card, a grid cell, a flex item -
       * takes 640 from it, the observer then measures 640 back, and the chart settles at
       * its fallback inside a 343px phone. A CSS width of 100% contributes nothing to
       * max-content, so the container decides and the attribute only sets the coordinate
       * system, which the viewBox keeps in step.
       */
      className="w-full"
    >
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={left}
            x2={left + plotWidth}
            y1={y(tick)}
            y2={y(tick)}
            className="stroke-line-subtle"
            strokeWidth={1}
          />
          <text
            x={left - AXIS_GAP_PX}
            y={y(tick) + 4}
            textAnchor="end"
            className="fill-ink-muted text-caption tabular-nums"
          >
            {truncateLabel(format(tick), valueGutter - AXIS_GAP_PX)}
          </text>
        </g>
      ))}
      {domain[0] < 0 && (
        <line
          x1={left}
          x2={left + plotWidth}
          y1={zero}
          y2={zero}
          className="stroke-line-strong"
          strokeWidth={1}
        />
      )}

      {series.map((entry, seriesIndex) => {
        const color = seriesColor(entry.tone, seriesIndex, series.length);
        const points = categories.map((category) => ({
          key: category.key,
          value: valueAt(entry, category.key),
        }));

        const path = line<{ key: string; value: number | null }>()
          .defined((point) => point.value !== null)
          .curve(curveLinear)
          .x((point) => at(point.key))
          .y((point) => y(point.value ?? 0))(points);

        const filledPath =
          filled && series.length === 1
            ? area<{ key: string; value: number | null }>()
                .defined((point) => point.value !== null)
                .curve(curveLinear)
                .x((point) => at(point.key))
                .y0(y(Math.max(domain[0], 0)))
                .y1((point) => y(point.value ?? 0))(points)
            : null;

        return (
          <g key={entry.id}>
            {filledPath !== null && (
              <path
                d={filledPath}
                className={cx(fillClass(color), 'opacity-15', animate && 'chart-fade-in')}
              />
            )}
            {path !== null && (
              <path
                d={path}
                pathLength={1}
                fill="none"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className={cx(strokeClass(color), animate && 'chart-draw-line')}
              />
            )}
            {points.map((point, pointIndex) => {
              if (point.value === null) return null;
              const category = categories[pointIndex];
              if (category === undefined) return null;
              const focused =
                focus !== null && focus.series === seriesIndex && focus.point === pointIndex;
              /**
               * ONE MARK PER POINT, AND IT IS ALWAYS THERE. A line with no dots is a shape
               * a mouse cannot hit and a single-point series is invisible entirely - which
               * is exactly the "one point" state the gallery renders. Small enough not to
               * turn a thirty-point line into a string of beads.
               */
              return (
                <Tooltip
                  key={category.key}
                  label={`${entry.label}, ${category.label}: ${format(point.value)}`}
                >
                  <circle
                    cx={at(point.key)}
                    cy={y(point.value)}
                    r={focused ? 5 : 3}
                    className={cx(
                      fillClass(color),
                      focused && 'stroke-2 stroke-accent',
                      animate && 'chart-fade-in',
                    )}
                  />
                </Tooltip>
              );
            })}
          </g>
        );
      })}

      {categories.map((category, index) => {
        if (index % step !== 0) return null;
        const centre = at(category.key);
        const label = truncateLabel(
          category.label,
          labelBudget(centre, 0, width, x.step() * step - AXIS_GAP_PX),
        );
        if (label === '') return null;
        return (
          <text
            key={category.key}
            x={centre}
            y={top + plotHeight + AXIS_LINE_PX}
            textAnchor="middle"
            className="fill-ink-muted text-caption"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}
